<template>
	<div class="p-p-2" :class="{'content': !isContext && !showNoLlmClassHint, 'child-content': !isContext && showNoLlmClassHint}">
		<Message v-if="showNoLlmClassHint" severity="warn" :closable="false">
			{{ isContext ? $t('Squeeze.Prompts.NoLlmDocumentClassHint') : $t('Squeeze.Prompts.NoLlmClassHint') }}
		</Message>
		<PromptList
			:rows="filteredPrompts"
			:documentClasses="dialogDocumentClasses"
			:allFields="allFields"
			:companyOptions="companyOptions"
			:creditorOptions="creditorOptions"
			:loading="isContext ? contextLoading : promptsLoading"
			:hasLlmClass="hasLlmClass"
			:showNoLlmClassHint="showNoLlmClassHint"
			:context="isContext"
			:lineItem="lineItem"
			:exporting="exporting"
			:importing="importing"
			@openEntryDialog="openEntryDialog"
			@deleteEntry="deleteEntry"
			@inlineEdit="onInlineEdit"
			@exportPrompts="exportPrompts"
			@importPrompts="openImportFileSelection"
		/>

		<!-- File selection of the import. Opened by the import button of the table. -->
		<input
			v-if="!isContext"
			ref="importFileInput"
			class="import-file-input"
			type="file"
			accept="application/json,.json"
			@change="onImportFileSelected"
		/>

		<PromptImportErrorDialog
			v-if="!isContext"
			:show="showImportErrors"
			:errors="importErrors"
			:loading="importing"
			@onClose="onCloseImportErrors"
			@onConfirm="startImportIgnoringErrors"
		/>

		<EntryDialog
			:show="showDialog"
			@onClose="onCloseDialog"
			@onConfirm="saveFromDialog"
			:loading="saving"
			:message="message"
			:messageType="messageType"
			:headerText="headerText"
			:showKeepDialogOpen="!promptEntry.id"
			:width="'60rem'"
		>
			<template #content>
				<PromptForm
					ref="promptFormElement"
					:promptEntry="promptEntry"
					:documentClasses="dialogDocumentClasses"
					:fields="fields"
					:companyOptions="companyOptions"
					:creditorOptions="creditorOptions"
					:showErrorMessage="showErrorMessage"
					:readonlyFieldName="isContext"
					@update="onUpdate"
					@classChanged="onClassChanged"
				/>
			</template>
		</EntryDialog>
	</div>
</template>

<script lang="ts">
import {TranslatedDescription} from "@/util/TranslationHelper";
/* eslint max-lines: off */
import {computed, defineComponent, onMounted, ref, reactive, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import EntryDialog from "@/components/EntryDialog.vue";
import PromptList from "@/apps/administration/components/prompts/PromptList.vue";
import Message from "primevue/message";
import PromptForm from "@/apps/administration/components/prompts/PromptForm.vue";
import PromptImportErrorDialog from "@/apps/administration/components/prompts/PromptImportErrorDialog.vue";
import {downloadFile} from "@/util/Export";
import {BatchClass, BatchClassSetting, DocumentClass, DocumentClassSetting, DocumentField, DocumentTable, DocumentTableColumn, LlmPrompt, LlmPromptDataType, LlmPromptImportError, LlmPromptImportResult, LlmPromptTransfer, MasterDataCompanyEntry, MasterDataPartnerEntry} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import {LLM_EXTRACTION_SETTING} from "@/composables/useDocumentClassSettings";

/** A table column as delivered by the API (which also returns "translatedDescription", untyped in the generated client) */
type TranslatableTableColumn = DocumentTableColumn & TranslatedDescription;

/** Suffix of the export file name, prefixed with the tenant of the export */
const EXPORT_FILE_SUFFIX = "-prompts.json";

/** File name of an export that does not name its tenant */
const DEFAULT_EXPORT_FILE_NAME = "prompts.json";

export default defineComponent({
	name: "PromptListView",
	components: {
		PromptList, PromptForm, PromptImportErrorDialog, EntryDialog, Message,
	},
	props: {
		/** false = header data (Kopfdaten), true = line items (Positionen). Also filters the prompts in context mode. */
		lineItem: {
			type: Boolean,
			default: false,
		},
		/** Field context: fixed document class the prompts are scoped to (enables context mode together with contextFieldName) */
		contextDocumentClassId: {
			type: Number,
			default: undefined,
		},
		/** Field context: fixed field name the prompts are scoped to (a header field name, or a table column name together with lineItem) */
		contextFieldName: {
			type: String,
			default: undefined,
		},
		/** Field context: has the context document class maxLLM enabled? (standalone mode determines this itself) */
		contextHasLlmClass: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();

		/** API for the LLM prompt endpoints */
		const llmPromptApi = ClientManager.getInstance().squeeze.llmPrompt;

		/** Document class API (for field / column lookups) */
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		/** Batch class API (to resolve the document classes of a batch class) */
		const batchClassApi = ClientManager.getInstance().squeeze.batchClass;

		/** Master data API (company / creditor dropdown data) */
		const masterDataApi = ClientManager.getInstance().squeeze.masterData;

		/** Master data options for the "Mandant" (company) field (empty => free text input) */
		const companyOptions = ref<MasterDataCompanyEntry[]>([]);

		/** Master data options for the "Geschäftspartner" (creditor) field: creditors + debitors merged (empty => free text input) */
		const creditorOptions = ref<MasterDataPartnerEntry[]>([]);

		/** Reference to the form component */
		const promptFormElement = ref<any>();

		/** Field context mode (embedded in a field's Prompts tab) */
		const isContext = computed(() => props.contextFieldName != null);

		/** Prompts loaded internally in standalone mode (this tab loads its own prompts) */
		const prompts = ref<LlmPrompt[]>([]);

		/** Loading indicator for the internally loaded standalone prompts */
		const promptsLoading = ref<boolean>(false);

		/** Prompts loaded internally in context mode */
		const contextPrompts = ref<LlmPrompt[]>([]);

		/** Loading indicator for the internally loaded context prompts */
		const contextLoading = ref<boolean>(false);

		/** The context document class (loaded to show its translated name in the dialog dropdown) */
		const contextDocumentClass = ref<DocumentClass | null>(null);

		/** Document classes that have the LLMExtraction setting active (standalone mode) */
		const llmDocumentClasses = ref<DocumentClass[]>([]);

		/** Is at least one class LLM-enabled? (standalone mode) */
		const hasLlmClassStandalone = ref<boolean>(false);

		/** Are the batch and documentclasses loaded (standalone mode) */
		const llmClassesLoading = ref<boolean>(true);

		/** Is at least one class LLM-enabled? (context: prop from parent, standalone: loaded internally) */
		const effectiveHasLlmClass = computed(() => isContext.value ? props.contextHasLlmClass : hasLlmClassStandalone.value);

		/** Show a hint that prompts cannot be created because no batch oder documentclass has LLM extraction enabled? */
		const showNoLlmClassHint = computed(() => !effectiveHasLlmClass.value && !(isContext.value ? false : llmClassesLoading.value));

		/** Fields selectable in the form (depending on the selected document class) */
		const fields = ref<DocumentField[]>([]);

		/** All selectable fields of the current tab (used by the inline table dropdown, filtered per row) */
		const allFields = ref<DocumentField[]>([]);

		/** Loading indicator for save / delete actions */
		const saving = ref<boolean>(false);

		/** Is an export running? */
		const exporting = ref<boolean>(false);

		/** Is an import running? */
		const importing = ref<boolean>(false);

		/** File input of the import (opened by the import button of the table) */
		const importFileInput = ref<HTMLInputElement | null>(null);

		/** The file selected for the import. Kept to repeat the import after the user ignored the errors. */
		const importFile = ref<File | null>(null);

		/** Prompts of the import file that cannot be imported because a reference is missing locally */
		const importErrors = ref<LlmPromptImportError[]>([]);

		/** Show the dialog with the prompts that cannot be imported? */
		const showImportErrors = ref<boolean>(false);

		/** Show the entry dialog? */
		const showDialog = ref<boolean>(false);

		/** Show validation errors? */
		const showErrorMessage = ref<boolean>(false);

		/** Is the current form invalid? */
		const isInvalid = ref<boolean>(true);

		/** Header text of the dialog */
		const headerText = ref<string>("");

		/** Message of the dialog */
		const message = ref<string>("");

		/** Message type of the dialog */
		const messageType = ref<string>("none");

		/** The entry currently being created / edited */
		const promptEntry = reactive<LlmPrompt>({
			fieldName: props.contextFieldName ?? "",
			documentClassId: props.contextDocumentClassId,
			dataType: LlmPromptDataType.Str,
			prompt: "",
			lineItem: props.lineItem,
			active: true,
		});

		/**
		 * Prompts shown in the table.
		 * - context mode: only prompts of the current field, for the current document class or wildcard (null)
		 * - standalone: prompts of the current tab (header vs. line items)
		 */
		const filteredPrompts = computed(() => {
			if (isContext.value) {
				// The search route already returns the field's prompts for this document class + wildcard (null).
				// Added filter to only show prompts for the current line item if set
				return contextPrompts.value.filter(prompt => Boolean(prompt.lineItem) === props.lineItem);
			}
			return prompts.value.filter(prompt => Boolean(prompt.lineItem) === props.lineItem);
		});

		/** Document classes offered in the dialog dropdown (context: only the current class + wildcard option) */
		const dialogDocumentClasses = computed<DocumentClass[]>(() => {
			if (isContext.value) {
				return contextDocumentClass.value ? [contextDocumentClass.value] : [];
			}
			return llmDocumentClasses.value;
		});

		/**
		 * Ids of the document classes the LLM extraction can run for. A prompt only ever takes effect
		 * for such a class, because the schema sent to the llm is built from its fields
		 */
		const llmDocumentClassIds = computed<number[]>(() => llmDocumentClasses.value
			.map(documentClass => documentClass.id)
			.filter((id): id is number => id != null));

		/** Removes duplicate fields (by name), keeping the first occurrence */
		const dedupeFieldsByName = (entries: DocumentField[]): DocumentField[] => {
			const seen = new Set<string>();
			const result: DocumentField[] = [];
			entries.forEach(entry => {
				if (entry.name && !seen.has(entry.name)) {
					seen.add(entry.name);
					result.push(entry);
				}
			});
			return result;
		};

		/**
		 * Resolves which document classes the field list should be built from:
		 * - a selected document class -> only that one
		 * - only a batch class selected -> its document classes that have the extraction enabled
		 * - nothing selected (wildcard) -> every document class that has the extraction enabled
		 * @param batchClassId Selected batch class (null = wildcard)
		 * @param documentClassId Selected document class (null = wildcard)
		 */
		const resolveTargetDocumentClassIds = async (batchClassId?: number | null, documentClassId?: number | null): Promise<number[]> => {
			if (documentClassId !== null && documentClassId !== undefined) {
				return [documentClassId];
			}

			if (batchClassId !== null && batchClassId !== undefined) {
				try {
					const classifications = await batchClassApi.getBatchClassClassifications(batchClassId);
					return (classifications || [])
						.map(classification => classification.documentClassId)
						.filter((id): id is number => id != null && llmDocumentClassIds.value.includes(id));
				} catch (e) {
					return [];
				}
			}

			// Wildcard: the prompt is valid for every document class the extraction runs for
			return llmDocumentClassIds.value;
		};

		/** Loads the header fields of the given document classes */
		const fetchHeaderFields = async (targetIds: number[]): Promise<DocumentField[]> => {
			try {
				const headerFields: DocumentField[] = await documentClassApi.getAllFields() || [];
				const relevant = headerFields
					.filter(field => field.documentClassId != null && targetIds.includes(field.documentClassId));
				return dedupeFieldsByName(relevant);
			} catch (e) {
				return [];
			}
		};

		/** Loads all table columns of a single document class as selectable fields */
		const loadTableColumnsOfDocumentClass = async (documentClassId: number): Promise<DocumentField[]> => {
			const tables: DocumentTable[] = await documentClassApi.getAllDocumentClassTables(documentClassId).catch(() => []);
			const columnsPerTable: TranslatableTableColumn[][] = await Promise.all(
				tables
					.filter((table): table is DocumentTable & { id: number } => table.id != null)
					.map(table => documentClassApi.getAllDocumentClassTableColumns(documentClassId, table.id).catch((): TranslatableTableColumn[] => []))
			);
			return columnsPerTable
				.flat()
				.map(column => ({
					name: column.name,
					description: column.description,
					translatedDescription: column.translatedDescription,
					documentClassId,
				}));
		};

		/**
		 * Loads line item fields (table columns) across the given document classes
		 */
		const fetchLineItemFields = async (targetIds: number[]): Promise<DocumentField[]> => {
			try {
				const columnsPerClass = await Promise.all(targetIds.map(loadTableColumnsOfDocumentClass));
				return dedupeFieldsByName(columnsPerClass.flat());
			} catch (e) {
				return [];
			}
		};

		/** Loads the selectable fields of the current tab for the given document classes */
		const fetchFields = (targetIds: number[]): Promise<DocumentField[]> => {
			return props.lineItem ? fetchLineItemFields(targetIds) : fetchHeaderFields(targetIds);
		};

		/**
		 * Loads the form's selectable fields depending on the selected document class.
		 * @param batchClassId Selected batch class (null = wildcard)
		 * @param documentClassId Selected document class (null = wildcard)
		 */
		const loadFields = async (batchClassId?: number | null, documentClassId?: number | null) => {
			const targetIds = await resolveTargetDocumentClassIds(batchClassId, documentClassId);
			fields.value = await fetchFields(targetIds);
		};

		/** Loads all fields of the current tab (used by the inline table dropdown) */
		const loadAllFields = async () => {
			allFields.value = await fetchFields(llmDocumentClassIds.value);
		};

		/**
		 * Loads the prompts (and the context document class) for the field context.
		 * Uses the search route which returns the prompts of the field for the given document class AND the wildcard (null).
		 */
		const loadContextData = async () => {
			if (props.contextDocumentClassId == null || props.contextFieldName == null) {
				return;
			}
			contextLoading.value = true;
			try {
				const [prompts, documentClass] = await Promise.all([
					llmPromptApi.getLlmPromptsByDocumentClassAndField(props.contextDocumentClassId, props.contextFieldName).then(data => data || []),
					documentClassApi.getDocumentClassById(props.contextDocumentClassId).catch(() => null),
				]);
				contextPrompts.value = prompts;
				contextDocumentClass.value = documentClass;
			} catch (e) {
				contextPrompts.value = [];
			} finally {
				contextLoading.value = false;
			}
		};

		/** Loads the prompts for the current tab (standalone mode) */
		const loadPrompts = () => {
			promptsLoading.value = true;
			llmPromptApi.getLlmPrompts()
				.then(data => {
					prompts.value = data || [];
				})
				.catch((response: Response) => {
					(response.json ? response.json() : Promise.reject()).then((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ErrorLoadPrompts') + ": " + err.message);
					}).catch(() => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ErrorLoadPrompts'));
					});
				})
				.finally(() => {
					promptsLoading.value = false;
				});
		};

		/** Checks whether a batch class has the LLMExtraction setting active */
		const isBatchClassLlmEnabled = async (batchClassId: number): Promise<boolean> => {
			try {
				const settings: BatchClassSetting[] = await batchClassApi.getBatchClassSettings(batchClassId);
				return (settings || []).some(setting => setting.settingName === LLM_EXTRACTION_SETTING && setting.active === true && setting.value === 'true');
			} catch (e) {
				return false;
			}
		};

		/**
		 * Checks whether a document class has LLM extraction enabled
		 * @param documentClassId Document class to check
		 * @param inheritedFromBatchClass Is the extraction enabled for one of its batch classes?
		 */
		const isDocumentClassLlmEnabled = async (documentClassId: number, inheritedFromBatchClass: boolean): Promise<boolean> => {
			try {
				const settings: DocumentClassSetting[] = await documentClassApi.getDocumentClassSettings(documentClassId);
				const ownSetting = (settings || []).find(setting => setting.settingName === LLM_EXTRACTION_SETTING);

				return ownSetting ? ownSetting.value === 'true' : inheritedFromBatchClass;
			} catch (e) {
				return false;
			}
		};

		/**
		 * Document classes that inherit an enabled LLM extraction from one of the given batch classes.
		 * @param batchClasses Batch classes that have the LLM extraction enabled
		 */
		const getDocumentClassIdsOfBatchClasses = async (batchClasses: BatchClass[]): Promise<number[]> => {
			const classificationsPerBatchClass = await Promise.all(
				batchClasses.map(batchClass => batchClass.id == null
					? Promise.resolve([])
					: batchClassApi.getBatchClassClassifications(batchClass.id).catch(() => []))
			);

			return classificationsPerBatchClass
				.flat()
				.map(classification => classification.documentClassId)
				.filter((id): id is number => id != null);
		};

		/** Determines the LLM-enabled batch- and document classes, standalone mode */
		const loadLlmClasses = async () => {
			llmClassesLoading.value = true;
			try {
				const [allBatchClasses, allDocumentClasses] = await Promise.all([
					batchClassApi.getAllBatchClasses().then(data => data || []),
					documentClassApi.getAllDocumentClasses().then(data => data || []),
				]);

				const enabledBatchClasses = (await Promise.all(
					allBatchClasses.map(async (batchClass: BatchClass) =>
						batchClass.id != null && await isBatchClassLlmEnabled(batchClass.id) ? batchClass : null
					)
				)).filter((batchClass): batchClass is BatchClass => batchClass != null);

				const inheritingDocumentClassIds = await getDocumentClassIdsOfBatchClasses(enabledBatchClasses);

				const enabledDocumentClasses = (await Promise.all(
					allDocumentClasses.map(async (documentClass: DocumentClass) =>
						documentClass.id != null
							&& await isDocumentClassLlmEnabled(documentClass.id, inheritingDocumentClassIds.includes(documentClass.id))
							? documentClass
							: null
					)
				)).filter((documentClass): documentClass is DocumentClass => documentClass != null);

				llmDocumentClasses.value = enabledDocumentClasses;
				hasLlmClassStandalone.value = enabledDocumentClasses.length > 0;
			} catch (response) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ErrorLoadClasses'));
			} finally {
				llmClassesLoading.value = false;
			}
		};

		/** Loads the master data dropdown options (company / creditor + debitor) */
		const loadCompanyData = async () => {
			try {
				const data = await masterDataApi.getMasterDataDropdowns();
				companyOptions.value = data.companies || [];
				creditorOptions.value = [...(data.creditors || []), ...(data.debitors || [])];
			} catch (e) {
				companyOptions.value = [];
				creditorOptions.value = [];
			}
		};

		/** Reloads the data after a change (context: internal reload, standalone: reload own prompts) */
		const reloadAfterChange = () => {
			if (isContext.value) {
				loadContextData();
			} else {
				loadPrompts();
			}
		};

		/** Resets the entry to its default values (context: keeps the fixed field name and document class) */
		const resetEntry = () => {
			Object.assign(promptEntry, {
				id: undefined,
				batchClassId: null,
				documentClassId: isContext.value ? props.contextDocumentClassId : null,
				company: null,
				creditor: null,
				fieldName: isContext.value ? props.contextFieldName : "",
				dataType: LlmPromptDataType.Str,
				prompt: "",
				lineItem: props.lineItem,
				active: true,
			});
		};

		/** Opens the create / edit dialog */
		const openEntryDialog = (data: LlmPrompt) => {
			message.value = "";
			messageType.value = "none";
			showErrorMessage.value = false;
			fields.value = [];

			if (data && data.id) {
				headerText.value = t('Squeeze.General.ChangeEntry', {entryName: t('Squeeze.Prompts.Prompt')});
				Object.assign(promptEntry, data);
				if (!isContext.value) {
					loadFields(data.batchClassId, data.documentClassId);
				}
			} else {
				headerText.value = t('Squeeze.General.CreateEntry', {entryName: t('Squeeze.Prompts.Prompt')});
				resetEntry();
				if (!isContext.value) {
					// Wildcard by default -> preload all available fields
					loadFields(null, null);
				}
			}

			showDialog.value = true;
		};

		/** Closes the dialog */
		const onCloseDialog = () => {
			showDialog.value = false;
			showErrorMessage.value = false;
			isInvalid.value = true;
		};

		/**
		 * Triggered on update of the form
		 * @param data Current form values
		 * @param invalid Is the form invalid?
		 */
		const onUpdate = (data: LlmPrompt, invalid: boolean) => {
			isInvalid.value = invalid;
			Object.assign(promptEntry, data);
		};

		/**
		 * Triggered when the batch- or document class changes in the form
		 * @param batchClassId Selected batch class
		 * @param documentClassId Selected document class
		 */
		const onClassChanged = (batchClassId: number | null, documentClassId: number | null) => {
			if (isContext.value) {
				// Field name is fixed in context mode, no field list to reload
				return;
			}
			loadFields(batchClassId, documentClassId);
		};

		/** Builds a clean request body */
		const buildBody = (entry: LlmPrompt): LlmPrompt => {
			// null = wildcard. The generated client types these fields as non-null,
			// so build a plain object and cast to keep the explicit null values.
			return {
				id: entry.id ?? undefined,
				batchClassId: entry.batchClassId ?? null,
				documentClassId: entry.documentClassId ?? null,
				company: entry.company ? entry.company : null,
				creditor: entry.creditor ? entry.creditor : null,
				fieldName: entry.fieldName,
				dataType: entry.dataType,
				prompt: entry.prompt,
				lineItem: entry.lineItem ?? props.lineItem,
				active: entry.active ?? false,
			} as unknown as LlmPrompt;
		};

		/**
		 * Saves the current entry
		 * @param keepDialogOpen Keep the dialog open after saving (new entries only)
		 */
		const saveFromDialog = (keepDialogOpen = false) => {
			if (isInvalid.value) {
				showErrorMessage.value = true;
				return;
			}

			saving.value = true;
			const body = buildBody(promptEntry);

			const promise = body.id
				? llmPromptApi.putLlmPrompt(body.id, body)
				: llmPromptApi.postLlmPrompt(body);

			promise
				.then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess'));
					if (!keepDialogOpen) {
						showDialog.value = false;
					} else {
						resetEntry();
						isInvalid.value = true;
						showErrorMessage.value = false;
					}
					reloadAfterChange();
				})
				.catch((response: Response) => {
					(response.json ? response.json() : Promise.reject()).then((err: { message: string }) => {
						message.value = err.message;
						messageType.value = "error";
					}).catch(() => {
						message.value = t('Squeeze.General.SaveError');
						messageType.value = "error";
					});
				})
				.finally(() => {
					saving.value = false;
				});
		};

		/**
		 * Persists an inline edit
		 * @param data Edited row
		 */
		const onInlineEdit = (data: LlmPrompt) => {
			if (!data.id) {
				return;
			}
			saving.value = true;
			llmPromptApi.putLlmPrompt(data.id, buildBody(data))
				.then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess'));
					reloadAfterChange();
				})
				.catch((response: Response) => {
					(response.json ? response.json() : Promise.reject()).then((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.SaveError') + ": " + err.message);
					}).catch(() => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.SaveError'));
					});
					reloadAfterChange();
				})
				.finally(() => {
					saving.value = false;
				});
		};

		/**
		 * Deletes a prompt
		 * @param data Row to delete
		 */
		const deleteEntry = (data: LlmPrompt) => {
			if (!data || !data.id) {
				return;
			}
			saving.value = true;
			llmPromptApi.deleteLlmPrompt(data.id)
				.then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.Prompts.DeleteSuccess'));
					reloadAfterChange();
				})
				.catch((response: Response) => {
					(response.json ? response.json() : Promise.reject()).then((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.DeleteError') + ": " + err.message);
					}).catch(() => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.DeleteError'));
					});
				})
				.finally(() => {
					saving.value = false;
				});
		};

		// region export / import

		/**
		 * Downloads the prompts selected in the table
		 * @param prompts Prompts selected via checkbox
		 */
		const exportPrompts = (prompts: LlmPrompt[]) => {
			const ids = prompts.map(prompt => prompt.id).filter((id): id is number => id != null);
			if (!ids.length) {
				return;
			}

			/* show loading indicator */
			exporting.value = true;
			llmPromptApi.postLlmPromptExport({elements: ids})
				.then((transfer: LlmPromptTransfer) => {
					const fileName = transfer.tenant ? transfer.tenant + EXPORT_FILE_SUFFIX : DEFAULT_EXPORT_FILE_NAME;
					downloadFile(fileName, JSON.stringify(transfer, null, 4));
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.Prompts.ExportSuccess'));
				})
				.catch((response: Response) => {
					(response.json ? response.json() : Promise.reject()).then((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ExportError') + ": " + err.message);
					}).catch(() => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ExportError'));
					});
				})
				.finally(() => {
					// disabled loading indicator
					exporting.value = false;
				});
		};

		/** Opens the file selection of the import (file browser from system / browser)*/
		const openImportFileSelection = () => {
			importFileInput.value?.click();
		};

		/**
		 * Uploads the selected file
		 * @param file The selected JSON file
		 * @param ignoreUnresolved Import the resolvable prompts and skip the ones the user decided to ignore
		 */
		const uploadImportFile = (file: File, ignoreUnresolved: boolean): Promise<LlmPromptImportResult> => {
			const body = new FormData();
			body.set("file", file);

			// Manual file upload to the Squeeze API. This has been programmed because the generated API client does not
			// support multipart/form-data requests: https://github.com/swagger-api/swagger-codegen/issues/3921
			return ClientManager.getInstance().customFetch(
				ClientManager.getInstance().getSqueezeBasePath() + "/llmPrompts/import?ignoreUnresolved=" + ignoreUnresolved,
				{method: "POST", body: body}
			).then((response: Response) => {
				if (response.status !== 200 && response.status !== 204) {
					return Promise.reject(response);
				}

				return response.json() as Promise<LlmPromptImportResult>;
			});
		};

		/**
		 * Imports the selected file. When the backend cannot match all prompts, nothing has been imported
		 * and the affected prompts are shown in a dialog first.
		 * @param file The selected JSON file
		 * @param ignoreUnresolved Import the resolvable prompts and skip the unresolvable ones
		 */
		const importPrompts = (file: File, ignoreUnresolved: boolean) => {
			importing.value = true;
			uploadImportFile(file, ignoreUnresolved)
				.then((result: LlmPromptImportResult) => {
					if (result.errors.length && !ignoreUnresolved) {
						importErrors.value = result.errors;
						showImportErrors.value = true;
						return;
					}

					showImportErrors.value = false;
					importErrors.value = [];
					importFile.value = null;
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.Prompts.ImportSuccess'));
					reloadAfterChange();
				})
				.catch((response: Response) => {
					(response.json ? response.json() : Promise.reject()).then((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ImportError') + ": " + err.message);
					}).catch(() => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ImportError'));
					});
				})
				.finally(() => {
					importing.value = false;
				});
		};

		/**
		 * Triggered when a file has been selected for the import
		 * @param event Change event of the file input
		 */
		const onImportFileSelected = (event: Event) => {
			const input = event.target as HTMLInputElement;
			if(input.files && input.files?.length > 1) {
				// not possible, but we never know
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Prompts.ImportError'));
			}

			const file = input.files?.[0];

			// Reset the input, so selecting the same file again triggers the change event
			input.value = "";

			if (!file) {
				return;
			}

			importFile.value = file;
			importPrompts(file, false);
		};

		/** Imports the prompts of the selected file, skipping the ones the user has ignored */
		const startImportIgnoringErrors = () => {
			if (!importFile.value) {
				return;
			}

			importPrompts(importFile.value, true);
		};

		/** Closes the dialog of the unresolvable prompts and aborts the import */
		const onCloseImportErrors = () => {
			showImportErrors.value = false;
			importErrors.value = [];
			importFile.value = null;
		};

		// endregion

		onMounted(() => {
			// Master data dropdown options are needed in both modes
			loadCompanyData();

			if (isContext.value) {
				// Field context: load the field's prompts + the context document class
				loadContextData();
			} else {
				// Standalone: load this tab's prompts, the LLM-enabled classes and preload all fields for the inline table dropdown
				loadPrompts();
				loadLlmClasses();
				loadAllFields();
			}
		});

		// Standalone: reload the inline dropdown fields when the available document classes change
		watch(llmDocumentClasses, () => {
			if (!isContext.value) {
				loadAllFields();
			}
		});

		return {
			t,
			promptFormElement,
			fields,
			allFields,
			companyOptions,
			creditorOptions,
			isContext,
			showNoLlmClassHint,
			promptsLoading,
			contextLoading,
			dialogDocumentClasses,
			hasLlmClass: effectiveHasLlmClass,
			saving,
			showDialog,
			showErrorMessage,
			headerText,
			message,
			messageType,
			promptEntry,
			filteredPrompts,
			openEntryDialog,
			onCloseDialog,
			onUpdate,
			onClassChanged,
			saveFromDialog,
			onInlineEdit,
			deleteEntry,
			exporting,
			importing,
			importFileInput,
			importErrors,
			showImportErrors,
			exportPrompts,
			openImportFileSelection,
			onImportFileSelected,
			startImportIgnoringErrors,
			onCloseImportErrors,
		};
	},
});
</script>

<style scoped>
.import-file-input {
	display: none;
}

.content {
	height: calc(100vh - 9rem);
}

.child-content {
	height: calc(100vh - 14rem);
}

::v-deep(div.p-message) {
	margin-top: 0;
}
</style>
